<template>
  <div :class="gradientType" />
</template>

<script setup lang="ts">
import { computed, useCssModule } from 'vue'

import { isImageBackground } from '../../shared-components/Background/Background.utils'

import type { CampaignBlockProps } from './Campaign.vue'

const { campaign } = defineProps<{
  campaign: CampaignBlockProps['campaigns'][number]
}>()

const styles = useCssModule()

const backgroundMode = computed(() => {
  if (!campaign.background) return undefined
  if (!isImageBackground(campaign.background)) return undefined

  return campaign.background.mode
})

const gradientType = computed(() => {
  const mode = backgroundMode.value
  if (!mode) return undefined

  const {
    heroDarkOverlay,
    heroLightOverlay,
    oneUpDarkOverlay,
    oneUpLightOverlay,
    twoUpDarkOverlay,
    twoUpLightOverlay,
  } = styles

  switch (campaign.campaignVariant) {
    case 'hero':
      return mode === 'dark' ? heroDarkOverlay : heroLightOverlay
    case 'oneUp':
      return mode === 'dark' ? oneUpDarkOverlay : oneUpLightOverlay
    case 'twoUp':
      return mode === 'dark' ? twoUpDarkOverlay : twoUpLightOverlay
    default:
      return ''
  }
})
</script>

<style module lang="css">
.overlayDesktop {
  @apply absolute top-0 bottom-0 left-0 right-0 inset-0;
}

.overlayMobileHero {
  @apply absolute top-0 -bottom-24 -left-24 -right-24 inset-0;
}

.heroDarkOverlay {
  @apply overlayMobileHero;

  background: linear-gradient(
    to top,
    rgba(17, 15, 21, 0.85) 16%,
    rgba(17, 15, 21, 0) 100%
  );
  margin-top: -96px;

  @media (min-width: theme('screens.md')) {
    @apply overlayDesktop;

    margin-top: 0;
    background: linear-gradient(
      to right,
      rgba(17, 15, 21, 0.85) 0%,
      rgba(17, 15, 21, 0) 60%
    );
  }
}

.heroLightOverlay {
  @apply overlayMobileHero;

  background: linear-gradient(
    to top,
    rgba(239, 241, 246, 0.85) 16%,
    rgba(239, 241, 246, 0) 100%
  );
  margin-top: -96px;

  @media (min-width: theme('screens.md')) {
    @apply overlayDesktop;

    margin-top: 0;
    background: linear-gradient(
      to right,
      rgba(239, 241, 246, 0.85) 0%,
      rgba(239, 241, 246, 0) 60%
    );
  }
}

.oneUpDarkOverlay {
  position: absolute;
  inset: -1rem;
  width: 1251px;
  margin-top: -112px;

  background: linear-gradient(
    to top right,
    rgba(17, 15, 21, 0.85) 16%,
    rgba(17, 15, 21, 0) 50%
  );

  @media (min-width: theme('screens.md')) {
    background: linear-gradient(
      24deg,
      rgba(17, 15, 21, 0.85) 16%,
      rgba(17, 15, 21, 0) 48%
    );

    width: 1592px;
    inset: -1.5rem;
    margin-top: 0;
  }
}

.oneUpLightOverlay {
  position: absolute;
  inset: -1rem;
  width: 1251px;
  margin-top: -112px;

  background: linear-gradient(
    to top right,
    rgba(239, 241, 246, 0.85) 16%,
    rgba(239, 241, 246, 0) 52%
  );

  @media (min-width: theme('screens.md')) {
    background: linear-gradient(
      24deg,
      rgba(239, 241, 246, 0.85) 16%,
      rgba(239, 241, 246, 0) 52%
    );

    width: 1592px;
    inset: -1.5rem;
    margin-top: 0;
  }
}

.twoUpDarkOverlay {
  position: absolute;
  inset: -1rem;
  width: 1251px;
  margin-top: -112px;

  background: linear-gradient(
    to top right,
    rgba(17, 15, 21, 0.85) 16%,
    rgba(17, 15, 21, 0) 50%
  );

  @media (min-width: theme('screens.md')) {
    inset: -1.5rem;
  }
}

.twoUpLightOverlay {
  position: absolute;
  inset: -1rem;
  width: 1251px;
  margin-top: -112px;

  background: linear-gradient(
    to top right,
    rgba(239, 241, 246, 0.85) 16%,
    rgba(239, 241, 246, 0) 50%
  );

  @media (min-width: theme('screens.md')) {
    inset: -1.5rem;
  }
}
</style>
